import { useEffect, useState } from "react";
import {
  deleteCredential,
  fetchCredentials,
  patchCredential,
  refreshAllCredentialsApi,
  refreshCredentialApi,
  updateCredentialLabelApi,
  type Credential,
  type RefreshAllResponse,
  type RefreshResult,
  type RefreshStatus,
} from "../api";

type BusyState = { id: string; action: "refresh" | "delete" } | null;

export function Credentials() {
  const [items, setItems] = useState<Credential[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<BusyState>(null);
  const [lastResult, setLastResult] = useState<RefreshResult | null>(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [lastBatch, setLastBatch] = useState<RefreshAllResponse | null>(null);

  const refresh = () => {
    setError(null);
    fetchCredentials()
      .then((r) => setItems(r.items))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(refresh, []);

  const onRefreshOne = async (cred: Credential) => {
    setBusy({ id: cred.id, action: "refresh" });
    setLastResult(null);
    try {
      const r = await refreshCredentialApi(cred.id);
      setLastResult(r.result);
      // Optimismo: actualizar la fila localmente con la nueva credencial.
      setItems((prev) => prev?.map((c) => (c.id === cred.id ? r.credential : c)) ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const onRefreshAll = async () => {
    if (refreshingAll) return;
    const n = items?.length ?? 0;
    if (n === 0) return;
    if (!confirm(`Refrescar las ${n} credenciales ahora? Se descargan los movimientos y saldos de todas las cuentas en serie; puede tardar varios minutos.`)) {
      return;
    }
    setRefreshingAll(true);
    setError(null);
    setLastResult(null);
    setLastBatch(null);
    try {
      const r = await refreshAllCredentialsApi();
      setItems(r.items);
      setLastBatch(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshingAll(false);
    }
  };

  const onDelete = async (cred: Credential) => {
    const label = `${cred.entityCode} (${cred.userName || cred.userId || cred.id})`;
    if (!confirm(`¿Borrar credencial ${label}? No deja de funcionar el banco; solo dejaremos de refrescar automáticamente.`)) {
      return;
    }
    setBusy({ id: cred.id, action: "delete" });
    try {
      await deleteCredential(cred.id);
      setItems((prev) => prev?.filter((c) => c.id !== cred.id) ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Credenciales bancarias</h2>
          <p className="meta">
            Cada credencial corresponde a un usuario+banco autorizado. Se actualizan
            automáticamente todas las noches; también puedes refrescar una a mano aquí.
            Si una credencial muestra <span className="badge warn">Solo saldos</span>,
            haz click para activar la descarga de movimientos en el siguiente refresco.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefreshAll}
          disabled={refreshingAll || (items?.length ?? 0) === 0}
          title="Llama a Wealth Reader para todas las credenciales y descarga movimientos y saldos."
        >
          {refreshingAll
            ? `Refrescando ${items?.length ?? 0}…`
            : `↻ Refrescar todas${items ? ` (${items.length})` : ""}`}
        </button>
      </div>

      {error && <p className="error">Error: {error}</p>}

      {lastBatch && (
        <div className={lastBatch.failed === 0 ? "success" : "warning"}>
          {lastBatch.failed === 0 ? "✅" : "⚠️"} Refresco completado:
          {" "}OK <code>{lastBatch.ok}</code> · KO <code>{lastBatch.failed}</code> · total <code>{lastBatch.total}</code>
          {" "}en <code>{Math.round(lastBatch.durationMs / 1000)}s</code>
        </div>
      )}

      {lastResult && (
        <div className={lastResult.ok ? "success" : "warning"}>
          {lastResult.ok ? "✅" : "⚠️"} Refresco terminado: status=<code>{lastResult.status}</code>
          {lastResult.code !== 0 && <> code=<code>{lastResult.code}</code></>}
          {lastResult.operationId && <> op=<code>{lastResult.operationId}</code></>}
          {lastResult.payloadFilename && <> · payload <code>{lastResult.payloadFilename}</code></>}
          {lastResult.message && <> · {lastResult.message}</>}
        </div>
      )}

      {!error && items === null && <p>Cargando…</p>}
      {items !== null && items.length === 0 && (
        <p className="meta">
          No hay credenciales guardadas. Al conectar un banco desde "Conectar banco" se
          guardará automáticamente la credencial para futuros refrescos.
        </p>
      )}

      {items !== null && items.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>Etiqueta</th>
              <th>Entidad</th>
              <th>Titular</th>
              <th>NIF/CIF</th>
              <th>Productos</th>
              <th title="Días de movimientos que se piden en cada refresco (date_from = hoy - N)">Días</th>
              <th>Último refresco</th>
              <th>Estado</th>
              <th>Token</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const busyHere = busy?.id === c.id;
              return (
                <tr key={c.id}>
                  <td>
                    <LabelCell
                      credential={c}
                      onUpdated={(updated) =>
                        setItems((prev) => prev?.map((x) => (x.id === updated.id ? updated : x)) ?? null)
                      }
                    />
                  </td>
                  <td><code>{c.entityCode}</code></td>
                  <td>
                    {c.userName || "—"}
                    {c.contractName && (
                      <div className="meta" style={{ fontSize: "0.8em" }}>
                        Contrato: <code>{c.contractName}</code>
                      </div>
                    )}
                  </td>
                  <td><code>{c.userId || "—"}</code></td>
                  <td>
                    {c.productTypes}
                    <br />
                    <OnlyBalancesToggle
                      credential={c}
                      onUpdated={(updated) =>
                        setItems((prev) => prev?.map((x) => (x.id === updated.id ? updated : x)) ?? null)
                      }
                    />
                  </td>
                  <td>
                    <MovementDaysCell
                      credential={c}
                      onUpdated={(updated) =>
                        setItems((prev) => prev?.map((x) => (x.id === updated.id ? updated : x)) ?? null)
                      }
                    />
                  </td>
                  <td>{c.lastRefreshAt ? new Date(c.lastRefreshAt).toLocaleString() : "—"}</td>
                  <td><StatusBadge status={c.lastRefreshStatus} code={c.lastRefreshCode} /></td>
                  <td><code>{c.tokenPreview}</code></td>
                  <td className="actions">
                    <button
                      type="button"
                      onClick={() => onRefreshOne(c)}
                      disabled={busyHere || refreshingAll}
                    >
                      {busyHere && busy.action === "refresh" ? "Refrescando…" : "Refrescar"}
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => onDelete(c)}
                      disabled={busyHere || refreshingAll}
                    >
                      {busyHere && busy.action === "delete" ? "Borrando…" : "Borrar"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function LabelCell({
  credential,
  onUpdated,
}: {
  credential: Credential;
  onUpdated: (c: Credential) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(credential.label);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const updated = await updateCredentialLabelApi(credential.id, draft);
      onUpdated(updated);
      setEditing(false);
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <span style={{ display: "inline-flex", gap: "0.25rem", alignItems: "center" }}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Etiqueta…"
          style={{ font: "inherit", padding: "0.2rem 0.4rem", width: "10rem" }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setEditing(false);
              setDraft(credential.label);
            }
          }}
        />
        <button type="button" onClick={save} disabled={busy}>OK</button>
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      style={{ cursor: "pointer", color: credential.label ? "#0f172a" : "#94a3b8" }}
      title="Click para editar"
    >
      {credential.label || "(sin etiqueta)"}
    </span>
  );
}

function OnlyBalancesToggle({
  credential,
  onUpdated,
}: {
  credential: Credential;
  onUpdated: (c: Credential) => void;
}) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    const target = !credential.onlyBalances;
    const msg = target
      ? "¿Desactivar la descarga de movimientos? A partir del próximo refresco solo se descargarán saldos."
      : "¿Activar la descarga de movimientos? El próximo refresco descargará transacciones (puede tardar más).";
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      const updated = await patchCredential(credential.id, { onlyBalances: target });
      onUpdated(updated);
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };
  const label = credential.onlyBalances ? "Solo saldos" : "Con movimientos";
  const cls = credential.onlyBalances ? "badge warn" : "badge ok";
  const title = credential.onlyBalances
    ? "Click para activar la descarga de movimientos"
    : "Click para volver a solo saldos";
  return (
    <button
      type="button"
      className={cls}
      onClick={toggle}
      disabled={busy}
      title={title}
      style={{ cursor: "pointer", border: "none", font: "inherit" }}
    >
      {busy ? "…" : label}
    </button>
  );
}

function MovementDaysCell({
  credential,
  onUpdated,
}: {
  credential: Credential;
  onUpdated: (c: Credential) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(credential.movementDays));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 1 || n > 365) {
      alert("Introduce un número entre 1 y 365 días.");
      return;
    }
    setBusy(true);
    try {
      const updated = await patchCredential(credential.id, { movementDays: Math.round(n) });
      onUpdated(updated);
      setEditing(false);
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <span style={{ display: "inline-flex", gap: "0.25rem", alignItems: "center" }}>
        <input
          autoFocus
          type="number"
          min={1}
          max={365}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{ font: "inherit", padding: "0.2rem 0.4rem", width: "4rem" }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setEditing(false);
              setDraft(String(credential.movementDays));
            }
          }}
        />
        <button type="button" onClick={save} disabled={busy}>OK</button>
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      style={{ cursor: "pointer" }}
      title="Días de movimientos por refresco. Click para editar."
    >
      {credential.movementDays} d
    </span>
  );
}

function StatusBadge({ status, code }: { status: RefreshStatus; code: number }) {
  const map: Record<RefreshStatus, { label: string; cls: string }> = {
    never:     { label: "—",          cls: "badge" },
    ok:        { label: "OK",         cls: "badge ok" },
    error:     { label: `err ${code}`,cls: "badge warn" },
    needs_2fa: { label: "2FA",        cls: "badge warn" },
    broken:    { label: "Roto",       cls: "badge ko" },
  };
  const m = map[status];
  return <span className={m.cls}>{m.label}</span>;
}
